import { createJsonMemoryAccessors } from '@/services/storage'

import type { ComparisonHistoryEntry, RankingWeights } from '../types'
import { DEFAULT_RANKING_WEIGHTS } from '../types'

const HISTORY_KEY = 'masterhub.comparison.history.v1'
const WEIGHTS_KEY = 'masterhub.comparison.weights.v1'
const FAVORITES_KEY = 'masterhub.comparison.favorites.v1'

type Slot = 'history' | 'weights' | 'favorites'

const memory: Record<Slot, unknown> = {
  history: null,
  weights: null,
  favorites: null,
}

const { readJson, writeJson } = createJsonMemoryAccessors(memory)

export function resetComparisonMemory(): void {
  memory.history = null
  memory.weights = null
  memory.favorites = null
}

export class ComparisonHistoryRepository {
  list(): ComparisonHistoryEntry[] {
    return readJson(HISTORY_KEY, 'history', [])
  }

  add(entry: Omit<ComparisonHistoryEntry, 'id' | 'date'>): ComparisonHistoryEntry {
    const next: ComparisonHistoryEntry = {
      ...entry,
      id: crypto.randomUUID(),
      date: new Date().toISOString(),
    }
    writeJson(HISTORY_KEY, 'history', [next, ...this.list()].slice(0, 200))
    return next
  }

  clear(): void {
    writeJson(HISTORY_KEY, 'history', [])
  }
}

export class ComparisonConfigRepository {
  getWeights(): RankingWeights {
    return readJson(WEIGHTS_KEY, 'weights', DEFAULT_RANKING_WEIGHTS)
  }

  saveWeights(weights: RankingWeights): RankingWeights {
    writeJson(WEIGHTS_KEY, 'weights', weights)
    return weights
  }
}

export class ComparisonFavoritesRepository {
  list(): string[] {
    return readJson(FAVORITES_KEY, 'favorites', [])
  }

  toggle(supplier: string): string[] {
    const current = this.list()
    const next = current.includes(supplier)
      ? current.filter((s) => s !== supplier)
      : [...current, supplier]
    writeJson(FAVORITES_KEY, 'favorites', next)
    return next
  }

  isFavorite(supplier: string): boolean {
    return this.list().includes(supplier)
  }
}

export const comparisonHistoryRepository = new ComparisonHistoryRepository()
export const comparisonConfigRepository = new ComparisonConfigRepository()
export const comparisonFavoritesRepository = new ComparisonFavoritesRepository()
